/**
 * Was man in Claude sagen kann, wenn 99takes verbunden ist.
 *
 * WARUM ES DIESE SEITE GIBT: Nach dem Verbinden steht ein Kunde vor einem
 * leeren Eingabefeld und weiss nicht, was das Werkzeug kann. Die Werkzeuge
 * erklaeren sich dem Modell, nicht dem Menschen.
 *
 * WAS HIER STEHT, MUSS ES GEBEN. Jede Zeile hier entspricht einem Werkzeug in
 * `supabase/functions/_shared/sim/tools.ts`, jede Kostenangabe dem, was die
 * Werkzeugbeschreibung dort sagt. Wer ein Werkzeug umbenennt oder den Preis
 * aendert, aendert diese Seite mit. Der MCP bietet nur Werkzeuge an
 * (`tools/list`, `tools/call`), keine eigenen Prompts und keine Skills — die
 * Seite behauptet deshalb auch keine. Skills sind etwas, das der Kunde sich
 * selbst in Claude Code anlegt; dafuer steht unten eine Vorlage.
 *
 * Werkzeugnamen stehen klein dabei, nicht als Anleitung: man ruft sie nicht
 * selbst auf, Claude waehlt sie. Sie helfen, wenn jemand in der Antwort von
 * Claude liest „ich rufe plan_simulation auf" und wissen will, was das ist.
 */

import { useEffect, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { useSitzung } from '../session';
import { Abschnitt, AppRahmen, Kopierzeile, Marke, Panel, Seitenkopf, Verweis } from '../ui';
import { testsatz } from './Einfuehrung';
import SoSiehtEsAus from './SoSiehtEsAus';
import { Erkennen, ERKENNEN_UNTER, ERKENNEN_UNTER_EN } from './Einfuehrung';
import { sprache, t } from '../sprache';
import { ABGLEICH, AUSBAU, CHALLENGER, DESIGN, EINRICHTUNG, STANDARD, VERGLEICH_1ZU1, VERGLEICH_STANDARD, VORAUSWAHL } from '../preise';

type Phase = 'vorbereiten' | 'pruefen' | 'auswerten' | 'ideen';

interface Ablauf {
  id: string;
  phase: Phase;
  titel: string;
  /** Ein Satz fuer die Zeile; der lange Text steht unter „Wie es funktioniert". */
  kurz: string;
  /** Preis fuer das Etikett neben dem Titel. Der volle Satz steht in `kosten.text`. */
  preis: string;
  wozu: ReactNode;
  prompts: string[];
  werkzeuge: string[];
  kosten: { frei: boolean; text: ReactNode };
  achtung?: ReactNode;
}

export const ABLAEUFE: Ablauf[] = [
  {
    id: 'personas',
    phase: 'vorbereiten',
    titel: 'Zielgruppe aufbauen',
    kurz: 'Claude fragt nach Angebot und Markt, 99takes liest Wettbewerber und Kundenstimmen und entwirft die Personas.',
    preis: `${EINRICHTUNG} Credits einmalig`,
    wozu: (
      <>
        Bevor du etwas prüfst, brauchst du eine Zielgruppe, die zu deinem Angebot passt.
        Claude fragt dich nach Angebot, Markt und Traffic-Herkunft. 99takes liest die Seiten
        deiner Wettbewerber und sucht Kundenstimmen im Netz (Bewertungen, Foren). Claude leitet daraus Schmerzpunkte, eure Positionierung
        und die Geschichte ab, die eure Texte erzählen (Wendepunkt: bisher, ab jetzt), dann die Jobs,
        und entwirft erst danach die Personas. Positionierung und Geschichte liest nur der
        Schreiber, nie die Personas: sonst bestätigten sie, was gewollt ist. Dabei fragt Claude, wer deine Kernzielgruppe ist, also für wen
        das Angebot gemacht ist. Personas der erweiterten Zielgruppe bleiben im Set, das Urteil folgt aber der Kernzielgruppe.
      </>
    ),
    prompts: [
      'Lass uns Personas für mein Angebot bauen: [Produkt, Zielgruppe, Markt]. Der Traffic kommt über [Google-Suche nach …].',
      'Welche Zielgruppen habe ich?',
      'Markier in [Zielgruppe] die Personas [Name] und [Name] als erweiterte Zielgruppe. Die anderen sind unsere Kernzielgruppe.',
      'Entwirf für [Zielgruppe] unsere Positionierung und das Narrativ „bisher, ab jetzt“ und speicher es an der Zielgruppe.',
      'Trag für die Personas in [Zielgruppe] Belege nach. Schick jede Persona vollständig, nicht nur die Belege.',
    ],
    werkzeuge: ['list_persona_sets', 'start_research', 'read_competitors', 'find_voices', 'save_persona_set', 'revise_persona_set'],
    kosten: { frei: false, text: `${EINRICHTUNG} Credits, einmal je Zielgruppe, fällig beim Speichern. Fragen, Recherche, Wettbewerberseiten und Suche nach Kundenstimmen davor sind enthalten, Änderungen danach auch.` },
    achtung: (
      <>
        Ein Set ohne Belege ist erlaubt und heißt dann Hypothesen-Set. Die Einwände der
        Personas kommen dann ohne Quellenangabe, und das steht in jeder Antwort.
      </>
    ),
  },
  {
    id: 'planen',
    phase: 'vorbereiten',
    titel: 'Vor dem Run: Preis und Passung',
    kurz: 'Claude nennt die Entscheidungen und den Preis, bevor etwas abgebucht wird.',
    preis: 'kostenlos',
    wozu: (
      <>
        Claude nennt die Entscheidungen, die vor dem Run bei dir liegen (gegen welches Set,
        welche Jobs, welche Vergleichsfassung), und den Preis. Dazu zwei Prüfungen: ob die
        Awareness-Stufen des Sets zu den Lesern der Seite passen, und ob der größere Hebel
        überhaupt auf der Seite liegt, etwa wenn mehrere Anzeigen-Winkel auf dieselbe
        Landingpage laufen.
      </>
    ),
    prompts: [
      'Was kostet ein Run unserer Landingpage https://[deine-seite] gegen [Zielgruppe]?',
      'Wir fahren drei Anzeigen-Winkel auf eine Landingpage. Lohnt sich ein Copy-Test überhaupt?',
    ],
    werkzeuge: ['plan_simulation'],
    kosten: { frei: true, text: 'Kostenlos. Abgebucht wird erst beim Run selbst.' },
  },
  {
    id: 'ab-test',
    phase: 'pruefen',
    titel: 'Eine Seite per Link als A/B-Test',
    kurz: '99takes schreibt Fassung B, zeigt sie dir und testet sie gegen deine Seite.',
    preis: 'je Einzelurteil rund 1,5 Credits',
    wozu: (
      <>
        Du gibst die Adresse deiner Seite, 99takes macht den Rest. Zuerst nennt Claude den Preis
        je Umfang: nur der Einstieg, Einstieg und erster Abschnitt oder die ganze Seite. Dann
        schreibt 99takes aus der Recherche deiner Zielgruppe eine neue Fassung B und zeigt sie
        dir, bevor etwas getestet wird. Du gibst sie frei, änderst sie oder lässt sie neu
        schreiben. Erst danach lesen die Personas beide Fassungen, jede auch unter den Jobs der
        anderen (Persona × Job), und Claude meldet unterwegs, wie viele Einzelurteile fertig sind. Am
        Ende stehen Urteil, Einwände, der schwächste Job, der Umfang und auf Wunsch der Bericht.
        Sagst du nur „teste meine Seite“, fragt Claude zuerst, ob der Text, die Wirkung der
        Gestaltung oder beides geprüft werden soll, jeweils mit Preis.
      </>
    ),
    prompts: [
      'Mach einen A/B-Test für https://[deine-seite].',
      'Schreib die ganze Seite https://[deine-seite] als Challenger neu und teste sie gegen die heutige.',
      'Ändere in Fassung B die Headline in „[deine Headline]“ und teste dann.',
    ],
    werkzeuge: ['test_page', 'get_job'],
    kosten: {
      frei: false,
      text: <>Der Preis vorher kostet nichts. Beim Schreiben wird nur das Schreiben gebucht, der Vergleich erst nach deinem Ja. Je Einzelurteil (eine Persona unter einem Job) rund 1,5 Credits, lange Seiten mehr; 7 Personas mit 6 Jobs sind 42 Einzelurteile.</>,
    },
    achtung: (
      <>
        Ohne eigene Zielgruppe baut Claude sie zuerst auf, samt Wettbewerbern und
        Kundenstimmen. Aus genau dieser Recherche entsteht Fassung B; mit einer fertigen
        Zielgruppe wird sie allgemeiner.
      </>
    ),
  },
  {
    id: 'design',
    phase: 'pruefen',
    titel: 'Wie wirkt meine Seite? Der Design-Check',
    kurz: 'Die Personas scrollen durch deine Seite, am Desktop und am Handy, und sagen, wie sie auf sie wirkt.',
    preis: `bis ${DESIGN.maxCredits} Credits`,
    wozu: (
      <>
        Der A/B-Test prüft den Text. Wie die Seite aussieht, prüft der Design-Check: Jede
        Persona deiner Zielgruppe sieht die Seite selbst, Bildschirm für Bildschirm, am Desktop
        und am Handy. Sie vergibt Noten für den ersten Eindruck, ob die Seite für Leute wie sie
        gemacht scheint, wie seriös sie wirkt, ob sie findet, was sie sucht, und wie die Seite am
        Handy funktioniert, und sagt, was sie stört, was ihr fehlt und was sie ändern würde. Die
        Screenshots bekommst du als Link mit, damit du siehst, welche Seite geprüft wurde.
      </>
    ),
    prompts: [
      'Mach einen Design-Check für https://[deine-seite].',
      'Wie wirkt https://[deine-seite] auf meine Zielgruppe, am Handy und am Desktop?',
      'Prüf bei https://[deine-seite] beides: den Text und wie die Seite wirkt.',
    ],
    werkzeuge: ['check_design'],
    kosten: {
      frei: false,
      text: <>Den Preis nennt Claude vorher, das kostet nichts. Je Persona {DESIGN.jePersona.toLocaleString('de-DE')} Credits, höchstens {DESIGN.maxPersonas} Personas, also bis {DESIGN.maxCredits} Credits. Derselbe Check innerhalb von 24 Stunden kostet nichts.</>,
    },
    achtung: (
      <>
        Das ist der Eindruck der Personas, kein gemessenes Ergebnis. Er steht getrennt vom
        Urteil über den Text, und anders als dort gibt es noch keine Trefferquote.
      </>
    ),
  },
  {
    id: 'pruefen',
    phase: 'pruefen',
    titel: 'Eine Landingpage prüfen, mit deinen eigenen Fassungen',
    kurz: 'Wo die Personas aussteigen und welche deiner Fassungen besser trägt.',
    preis: 'je Einzelurteil rund 1,5 Credits',
    wozu: (
      <>
        Die ganze Seite gegen die Personas, am besten per URL. Dann wird sie wie im Browser
        geladen: die Personas lesen den kompletten Text samt Gliederung (was Headline ist, was
        Kleingedrucktes) und bekommen das Layout beschrieben: was über der Falz steht, wie
        viele Knöpfe konkurrieren, wie lang das Formular ist. Du bekommst je Persona Einwände,
        fehlende Elemente und an welchem Abschnitt sie aussteigen. Mit einer zweiten Fassung,
        etwa deiner überarbeiteten Seite, auch welche der beiden besser trägt. Hat das Set
        eine erweiterte Zielgruppe, zählt für das Urteil nur die Kernzielgruppe; gewinnt ein Text nur in der
        erweiterten Zielgruppe, steht eine Warnung statt eines Siegs. Nennst du dazu, wofür deine Marke steht, sagt jede
        Persona, ob der Text das Versprechen hält. Das zählt nicht in die Punkte.
      </>
    ),
    prompts: [
      'Nur Feedback, keine neue Fassung: Wo steigen die Leute auf https://[deine-seite] aus, und was fehlt ihnen? Gegen [Zielgruppe].',
      'Prüf die neue Fassung gegen die alte. Unser Versprechen: [was man nur bei uns bekommt, ein Satz].',
      'Prüf unsere Landingpage mit dem Simulationsmodell [slug].',
      'Hier ist der komplette Text unserer aktuellen und unserer neuen Landingpage. Welche trägt besser? Aktuell: „…“ Neu: „…“',
    ],
    werkzeuge: ['run_study', 'simulate_copy', 'list_persona_sets'],
    kosten: {
      frei: false,
      text: <>Im Vergleich liest jede Persona auch unter den Jobs der anderen (Persona × Job, Standard), höchstens {STANDARD.simRuns} Einzelurteile. Je Einzelurteil 1,5 Credits bei kurzen Texten, lange Seiten je weitere 8.000 Zeichen 0,5 mehr. Gegen eine Vergleichsfassung, die 99takes erst erzeugt, 1 Credit je 4.000 Zeichen dazu: {STANDARD.personas} Personas mit {STANDARD.jobs} Jobs kosten so {VERGLEICH_STANDARD}. Günstiger ist 1:1, jede Persona nur mit ihrem eigenen Job: {VERGLEICH_1ZU1}. Sag es Claude vor dem Run. Eine Seite allein ohne Vergleich läuft 1:1. Das Versprechen kostet nichts extra.</>,
    },
    achtung: (
      <>
        Eine Trefferquote entsteht nur aus einem Vergleich: eine Seite allein bekommt Gründe,
        aber kein Urteil, gegen das sich ein Marktergebnis prüfen ließe. Wenn du wissen willst,
        ob 99takes bei dir recht hat, lass die neue Fassung gegen die alte antreten. Ein
        Simulationsmodell ist eine feste Jury auf Claude, deren Trefferquote für genau diese
        Fassung gezählt wird.
      </>
    ),
  },
  {
    id: 'schreiben',
    phase: 'pruefen',
    titel: 'Fassungen schreiben lassen',
    kurz: 'Challenger-Fassungen aus Einwänden, Kundenstimmen oder deiner Recherche.',
    preis: `${CHALLENGER.von} bis ${CHALLENGER.bis} Credits`,
    wozu: (
      <>
        99takes schreibt Challenger-Fassungen, die gegen deinen heutigen Text antreten: aus den
        Einwänden eines Runs, aus Kundenstimmen und Narrativ deines Sets oder ganz ohne Vorlage
        als komplette Seite aus der Recherche. Auf Wunsch nach einer von dreizehn Methoden, etwa
        „Bisher, ab jetzt“, Awareness-Copy, nutzengetrieben, Beleg zuerst, aus Kundenstimmen,
        AIDA, Vorher-Nachher-Brücke, Gegen die Alternative oder nur neue Headlines. Ohne Angabe nimmt
        jede Fassung eine andere. Zahlen, die nirgends
        belegt sind, schreibt 99takes nicht: dann steht ein Platzhalter [BELEG: …] im Text.
      </>
    ),
    prompts: [
      'Schreib aus den Einwänden des letzten Runs drei bessere Fassungen und sortier sie vor.',
      'Schreib unsere Landingpage neu aus der Recherche in [Zielgruppe], nach „Bisher, ab jetzt“.',
      'Schreib nur die Headline nutzengetrieben, drei Fassungen: „[deine Headline]“.',
      'Schreib drei neue Headlines für unsere Seite, der Rest bleibt: „[deine Headline]“.',
    ],
    werkzeuge: ['write_challenger', 'preselect_copy', 'simulate_copy'],
    kosten: { frei: false, text: <>3 Credits plus je Fassung 1 je angefangene 4.000 Zeichen. Drei Fassungen einer Headline kosten {CHALLENGER.dreiKurz}, fünf einer langen Seite {CHALLENGER.bis}. Genau derselbe Auftrag ein zweites Mal kostet nichts.</> },
    achtung: (
      <>
        Stammen die Fassungen aus den Einwänden einer Jury, gewinnen sie gegen dieselbe Jury
        leicht. Das ist erwartbar und noch kein Beleg für den Markt. Härter wird der Test mit
        anderen Personas, entschieden wird er mit dem Marktergebnis.
      </>
    ),
  },
  {
    id: 'nur-ihr',
    phase: 'pruefen',
    titel: 'Was sagt nur ihr?',
    kurz: 'Welche deiner Aussagen auch Wettbewerber machen und welche nur ihr.',
    preis: `${ABGLEICH.von} bis ${ABGLEICH.bis} Credits`,
    wozu: (
      <>
        Deine Seite neben ein bis fünf Wettbewerbstexten. Je Kernaussage erfährst du, ob sie
        besetzt ist (ein Wettbewerber sagt dasselbe), umstritten (ähnlich, aber schwächer) oder
        frei, jeweils mit wörtlichem Zitat und Quelle. Steht eine freie Aussage weiter unten,
        ist sie der Kandidat für den Hero. Mit dem Namen einer Studie erscheint der Abgleich im
        Bericht als „Was nur ihr sagt“.
      </>
    ),
    prompts: [
      'Vergleich unsere Startseite https://[deine-seite] mit [Wettbewerber 1] und [Wettbewerber 2]. Welche Aussagen macht nur ihr?',
      'Schreib drei Fassungen der Hero, die die freie Aussage nach oben holen, und prüf die beste gegen die aktuelle.',
    ],
    werkzeuge: ['compare_claims', 'write_challenger', 'simulate_copy'],
    kosten: { frei: false, text: '2 Credits plus 1 je Wettbewerbstext, also 3 bis 7. Dieselben Texte ein zweites Mal kosten nichts.' },
    achtung: (
      <>
        Gemessen wird, was in den gelieferten Texten steht, nicht wie der Markt die Anbieter
        wahrnimmt. „Frei“ heißt: in diesen Texten frei. Mit zwei Wettbewerbern ist das ein
        schwächerer Befund als mit fünf.
      </>
    ),
  },
  {
    id: 'vorauswahl',
    phase: 'pruefen',
    titel: 'Mehrere Entwürfe vorsortieren',
    kurz: 'Aus zwei bis acht Entwürfen den finden, der den vollen Run wert ist.',
    preis: `${VORAUSWAHL.von} bis ${VORAUSWAHL.bis} Credits`,
    wozu: (
      <>
        Du hast zwei bis acht Fassungen und willst nur die beste voll prüfen. Eine kleine
        Jury aus 3 bis 5 Personas liest alle Fassungen blind und in zufälliger Reihenfolge.
        Das ist eine Vorauswahl und keine Messung. Liegen die besten zwei zu nah
        beieinander, sagt 99takes das statt einen Sieger zu nennen.
      </>
    ),
    prompts: [
      'Ich habe fünf Entwürfe für den Einstieg unserer Landingpage. Sortier sie gegen [Zielgruppe] vor, und prüf dann den besten richtig.',
    ],
    werkzeuge: ['preselect_copy', 'simulate_copy'],
    kosten: { frei: false, text: 'Bezahlt wird je Person der Jury, nicht je Fassung: fünf Entwürfe kosten so viel wie zwei.' },
  },
  {
    id: 'studie',
    phase: 'pruefen',
    titel: 'Mehrere Seiten vergleichen und teilen',
    kurz: 'Eine Tabelle über mehrere Seiten, als Bericht mit Link zum Weiterleiten.',
    preis: 'je Seite wie ein Run',
    wozu: (
      <>
        Mehrere Seiten gegen dasselbe Set, etwa deine Produktseiten aus einem Template oder
        deine Seite neben denen der Wettbewerber. Ergebnis ist eine Vergleichstabelle.
      </>
    ),
    prompts: [
      'Vergleich diese drei Seiten gegen [Zielgruppe]: [URL 1], [URL 2], [URL 3]. Nenn mir vorher den Preis.',
      'Mach aus der Studie einen Bericht mit Link, den ich weiterleiten kann.',
    ],
    werkzeuge: ['run_study', 'publish_report'],
    kosten: { frei: false, text: <>Je Seite wie ein Run: rund 1,5 Credits je Persona, lange Seiten mehr; mit erzeugter Vergleichsfassung 1 Credit je 4.000 Zeichen dazu. Der Bericht ist kostenlos.</> },
    // Seit 25.09.2026 (Entscheidung Robert): bei Marktsets gibt es den Link
    // direkt, bei einem EIGENEN Set nur nach ausdruecklicher Zustimmung
    // (publish_report mit share_private) — und jeder Bericht ist unter /runs
    // einsehbar, auch ohne Link. Vorher gab es fuer eigene Sets gar keinen.
    achtung: <>
      Gib alle Seiten gleich an: alle als URL oder alle als Text. Gemischt ist die Tabelle kein fairer
      Vergleich. Ein Bericht zu deinem eigenen Set bekommt einen Link erst, wenn du zustimmst. Wer ihn
      hat, sieht deine Persona-Recherche samt Belegen. Alle Berichte findest du unter{' '}
      <Verweis to="/runs">Runs</Verweis>, dort ziehst du einen Link auch wieder zurück.
    </>,
  },
  {
    id: 'ausbau',
    phase: 'pruefen',
    titel: 'Eine Seite Schritt für Schritt ausbauen',
    kurz: 'Abschnitt für Abschnitt den besten Entwurf finden, am Ende gegen das Original.',
    preis: `${AUSBAU.von} bis ${AUSBAU.bis} Credits`,
    wozu: (
      <>
        Der größte Ablauf: eine Seite Abschnitt für Abschnitt verbessern, in fester
        Reihenfolge. Je Schritt testest du mehrere Entwürfe, bestätigst den Sieger und
        sagst, warum. Am Ende tritt die zusammengesetzte Seite gegen die ursprüngliche an,
        vor allen Personas.
      </>
    ),
    prompts: [
      'Lass uns https://[deine-seite] gegen [Zielgruppe] ausbauen.',
      'Teste diese drei Entwürfe für die Hero: …',
      'Nimm Entwurf B. Grund: [warum]. Weiter zum nächsten Schritt.',
    ],
    werkzeuge: ['start_build', 'test_variants', 'finish_build'],
    kosten: { frei: false, text: `Eine ganze Seite mit ${STANDARD.personas} Personas rund ${AUSBAU.von} Credits, mit einer Verzweigung an der Hero bis ${AUSBAU.bis}. Kleinere Zielgruppen kosten weniger. Starten und Weiterschalten sind kostenlos.` },
    achtung: <>Ein einzelner Schritt ist in sich abgeschlossen, der ganze Ausbau nicht. Prüf vorher, ob dein Guthaben für alle Schritte reicht.</>,
  },
  {
    id: 'ergebnis',
    phase: 'auswerten',
    titel: 'Das echte Ergebnis zurückmelden',
    kurz: 'Welche Fassung im Markt gewonnen hat. Daraus entsteht deine Trefferquote.',
    preis: 'kostenlos',
    wozu: (
      <>
        Wenn eine geprüfte Fassung live war: melde, welche im Markt gewonnen hat, am besten
        mit Zahlen: Klicks und Impressionen oder Anfragen und Besuche. Daraus entsteht deine
        Trefferquote, also wie oft die Simulation bei dir richtig lag. Ergebnisse, die im
        Rauschen liegen, werden gespeichert, aber nicht gezählt. Das geht in Claude oder direkt
        unter <Verweis to="/runs">Runs</Verweis> mit „Ergebnis melden“. Für ein Simulationsmodell steht die Quote ab zehn entscheidbaren Ergebnissen dabei, vorher „noch nicht gemessen“.
      </>
    ),
    prompts: [
      'Die neue Landingpage aus dem Vergleich letzte Woche hat gewonnen: 83 gegen 52 Anfragen bei je 1.800 Besuchen.',
      'Wie oft lag 99takes bei mir bisher richtig?',
      'Welche Simulationsmodelle gibt es, und wie oft lagen sie richtig?',
    ],
    werkzeuge: ['report_outcome', 'list_persona_sets'],
    kosten: { frei: true, text: 'Kostenlos.' },
  },
  {
    id: 'bibliothek',
    phase: 'ideen',
    titel: 'Ideen aus der Funnel-Bibliothek',
    kurz: 'Echte B2B-Funnels nach Branche durchsuchen und Schritt für Schritt zerlegen lassen.',
    preis: 'kostenlos',
    wozu: (
      <>
        Echte B2B-Funnels (Landingpages, Demo-Anfragen, Testzugänge, Quiz, Rechner,
        Preisseiten) nach Branche und Art durchsuchen, einen Schritt für Schritt zerlegen
        lassen oder die häufigsten Muster einer Branche sehen.
      </>
    ),
    prompts: [
      'Zeig mir Demo-Anfrage-Funnels aus der Branche ERP im DACH-Raum.',
      'Welche Headline-Muster sind bei CMS-Anbietern im Einstieg am häufigsten?',
    ],
    werkzeuge: ['find_funnels'],
    kosten: { frei: true, text: 'Kostenlos.' },
  },
];

/** Die englische Fassung je Ablauf, über die id. Beim Rendern über `inSprache` gewählt. */
type AblaufEn = Pick<Ablauf, 'titel' | 'kurz' | 'preis' | 'wozu' | 'prompts' | 'achtung'> & { kosten: ReactNode };

export const ABLAEUFE_EN: Record<string, AblaufEn> = {
  personas: {
    titel: 'Build an audience',
    kurz: 'Claude asks about your offer and market, 99takes reads competitors and customer voices and drafts the personas.',
    preis: `${EINRICHTUNG} credits once`,
    wozu: (
      <>
        Before you test anything, you need an audience that fits your offer.
        Claude asks you about your offer, market and where your traffic comes from. 99takes reads your
        competitors' pages and searches the web for customer voices (reviews, forums). From that, Claude derives pain points, your positioning
        and the story your copy tells (turning point: before, from now on), then the jobs,
        and only then drafts the personas. Only the writer reads positioning and story, never the
        personas: otherwise they would confirm what is intended. Claude also asks who your core audience is, meaning who
        the offer is made for. Personas in the extended audience stay in the set, but the verdict follows the core audience.
      </>
    ),
    prompts: [
      "Let's build personas for my offer: [product, audience, market]. The traffic comes from [Google search for …].",
      'Which audiences do I have?',
      'In [audience], mark the personas [name] and [name] as extended audience. The others are our core audience.',
      'Draft our positioning and the “before, from now on” narrative for [audience] and save it to the audience.',
      'Add proof for the personas in [audience]. Send each persona in full, not just the proof.',
    ],
    kosten: `${EINRICHTUNG} credits, once per audience, charged when saving. Questions, research, competitor pages and the search for customer voices before that are included, and so are changes afterwards.`,
    achtung: (
      <>
        A set without proof is allowed and is then called a hypothesis set. The personas'
        objections then come without sources, and every answer says so.
      </>
    ),
  },
  planen: {
    titel: 'Before the run: price and fit',
    kurz: 'Claude names the decisions and the price before anything is charged.',
    preis: 'free',
    wozu: (
      <>
        Claude names the decisions that are yours before the run (which set, which jobs,
        which baseline) and the price. Plus two checks: whether the awareness stages of the
        set fit the readers of the page, and whether the bigger lever is on the page at all,
        for example when several ad angles run to the same landing page.
      </>
    ),
    prompts: [
      'What does a run of our landing page https://[your-page] against [audience] cost?',
      'We run three ad angles to one landing page. Is a copy test worth it at all?',
    ],
    kosten: 'Free. Credits are only charged for the run itself.',
  },
  'ab-test': {
    titel: 'A/B test a page by link',
    kurz: '99takes writes version B, shows it to you and tests it against your page.',
    preis: 'about 1.5 credits per single verdict',
    wozu: (
      <>
        You give the address of your page, 99takes does the rest. First Claude names the price
        by scope: just the opening, the opening and the first section, or the whole page. Then
        99takes writes a new version B from the research on your audience and shows it to you
        before anything is tested. You approve it, change it or have it rewritten. Only then do
        the personas read both versions, each also under the other personas' jobs (persona × job),
        and Claude reports along the way how many single verdicts are done. At the end you get the
        verdict, objections, the weakest job, the scope and, if you like, the report. If you only
        say “test my page”, Claude first asks whether the copy, the effect of the design or both
        should be tested, each with a price.
      </>
    ),
    prompts: [
      'Run an A/B test for https://[your-page].',
      "Rewrite the whole page https://[your-page] as a challenger and test it against today's.",
      'Change the headline in version B to “[your headline]” and then test.',
    ],
    kosten: <>The price preview is free. Writing only charges for the writing, the comparison only after your yes. About 1.5 credits per single verdict (one persona under one job), long pages more; 7 personas with 6 jobs are 42 single verdicts.</>,
    achtung: (
      <>
        Without your own audience, Claude builds it first, including competitors and customer
        voices. Version B comes from exactly this research; with a ready-made audience it gets
        more generic.
      </>
    ),
  },
  design: {
    titel: 'How does my page come across? The design check',
    kurz: 'The personas scroll through your page, on desktop and on mobile, and say how it comes across to them.',
    preis: `up to ${DESIGN.maxCredits} credits`,
    wozu: (
      <>
        The A/B test checks the copy. How the page looks is checked by the design check: each
        persona in your audience sees the page itself, screen by screen, on desktop and on
        mobile. They give scores for the first impression, whether the page seems made for people
        like them, how trustworthy it looks, whether they find what they are looking for and how
        the page works on mobile, and they say what bothers them, what they miss and what they
        would change. You get the screenshots as a link, so you can see which page was checked.
      </>
    ),
    prompts: [
      'Run a design check for https://[your-page].',
      'How does https://[your-page] come across to my audience, on mobile and on desktop?',
      'For https://[your-page], check both: the copy and how the page comes across.',
    ],
    kosten: <>Claude names the price first, that is free. {DESIGN.jePersona.toLocaleString('en-GB')} credits per persona, at most {DESIGN.maxPersonas} personas, so up to {DESIGN.maxCredits} credits. The same check within 24 hours is free.</>,
    achtung: (
      <>
        This is the personas' impression, not a measured result. It stands apart from the
        verdict on the copy, and unlike there, there is no hit rate yet.
      </>
    ),
  },
  pruefen: {
    titel: 'Test a landing page, with your own versions',
    kurz: 'Where the personas drop off and which of your versions works better.',
    preis: 'about 1.5 credits per single verdict',
    wozu: (
      <>
        The whole page against the personas, ideally by URL. Then it is loaded as in a browser:
        the personas read the complete text with its structure (what is headline, what is small
        print) and get the layout described: what is above the fold, how many buttons compete,
        how long the form is. For each persona you get objections, missing elements and the
        section where they drop off. With a second version, for example your revised page, also
        which of the two works better. If the set has an extended audience, only the core audience
        counts toward the verdict; if a text wins only in the extended audience, you get a warning
        instead of a win. If you also say what your brand stands for, each persona says whether
        the text keeps the promise. That does not count toward the scores.
      </>
    ),
    prompts: [
      'Feedback only, no new version: where do people drop off on https://[your-page], and what do they miss? Against [audience].',
      'Test the new version against the old one. Our promise: [what you only get from us, one sentence].',
      'Test our landing page with the simulation model [slug].',
      'Here is the complete text of our current and our new landing page. Which works better? Current: “…” New: “…”',
    ],
    kosten: <>In a comparison, each persona also reads under the other personas' jobs (persona × job, default), at most {STANDARD.simRuns} single verdicts. 1.5 credits per single verdict for short texts, long pages 0.5 more per additional 8,000 characters. Against a baseline that 99takes generates first, add 1 credit per 4,000 characters: {STANDARD.personas} personas with {STANDARD.jobs} jobs thus cost {VERGLEICH_STANDARD}. 1:1 is cheaper, each persona only with its own job: {VERGLEICH_1ZU1}. Tell Claude before the run. A single page without a comparison runs 1:1. The promise costs nothing extra.</>,
    achtung: (
      <>
        A hit rate only comes from a comparison: a single page gets reasons, but no verdict that
        a market result could be checked against. If you want to know whether 99takes is right
        for you, let the new version compete against the old one. A simulation model is a fixed
        jury on Claude whose hit rate is counted for exactly this version.
      </>
    ),
  },
  schreiben: {
    titel: 'Have versions written',
    kurz: 'Challenger versions from objections, customer voices or your research.',
    preis: `${CHALLENGER.von} to ${CHALLENGER.bis} credits`,
    wozu: (
      <>
        99takes writes challenger versions that compete against your current text: from the
        objections of a run, from the customer voices and narrative of your set, or without any
        template as a complete page from the research. On request with one of thirteen methods,
        for example “Before, from now on”, awareness copy, benefit-driven, proof first, from
        customer voices, AIDA, before-after bridge, against the alternative or just new headlines.
        Without a choice, each version takes a different one. Numbers that are not backed up
        anywhere, 99takes does not write: then a placeholder [BELEG: …] sits in the text.
      </>
    ),
    prompts: [
      "Write three better versions from the last run's objections and preselect them.",
      'Rewrite our landing page from the research in [audience], using “Before, from now on”.',
      'Write only the headline, benefit-driven, three versions: “[your headline]”.',
      'Write three new headlines for our page, the rest stays: “[your headline]”.',
    ],
    kosten: <>3 credits plus 1 per version for each started 4,000 characters. Three versions of a headline cost {CHALLENGER.dreiKurz}, five of a long page {CHALLENGER.bis}. The exact same request a second time is free.</>,
    achtung: (
      <>
        If the versions come from a jury's objections, they easily win against the same jury.
        That is expected and not yet proof for the market. The test gets harder with other
        personas, and the market result decides it.
      </>
    ),
  },
  'nur-ihr': {
    titel: 'What only you say',
    kurz: 'Which of your claims competitors make too, and which only you make.',
    preis: `${ABGLEICH.von} to ${ABGLEICH.bis} credits`,
    wozu: (
      <>
        Your page next to one to five competitor texts. For each key claim you learn whether it
        is taken (a competitor says the same), contested (similar, but weaker) or free, each with
        a verbatim quote and source. If a free claim sits further down, it is the candidate for
        the hero. With the name of a study, the comparison appears in the report as “What only
        you say”.
      </>
    ),
    prompts: [
      'Compare our homepage https://[your-page] with [competitor 1] and [competitor 2]. Which claims do only we make?',
      'Write three versions of the hero that bring the free claim to the top, and test the best one against the current one.',
    ],
    kosten: '2 credits plus 1 per competitor text, so 3 to 7. The same texts a second time are free.',
    achtung: (
      <>
        What is measured is what is in the texts provided, not how the market perceives the
        vendors. “Free” means: free in these texts. With two competitors that is a weaker
        finding than with five.
      </>
    ),
  },
  vorauswahl: {
    titel: 'Preselect several drafts',
    kurz: 'From two to eight drafts, find the one worth the full run.',
    preis: `${VORAUSWAHL.von} to ${VORAUSWAHL.bis} credits`,
    wozu: (
      <>
        You have two to eight versions and only want to fully test the best one. A small jury
        of 3 to 5 personas reads all versions blind and in random order. This is a preselection,
        not a measurement. If the best two are too close, 99takes says so instead of naming a
        winner.
      </>
    ),
    prompts: [
      'I have five drafts for the opening of our landing page. Preselect them against [audience], and then test the best one properly.',
    ],
    kosten: 'You pay per jury member, not per version: five drafts cost as much as two.',
  },
  studie: {
    titel: 'Compare and share several pages',
    kurz: 'One table across several pages, as a report with a link to forward.',
    preis: 'per page like a run',
    wozu: (
      <>
        Several pages against the same set, for example your product pages from one template or
        your page next to your competitors' pages. The result is a comparison table.
      </>
    ),
    prompts: [
      'Compare these three pages against [audience]: [URL 1], [URL 2], [URL 3]. Tell me the price first.',
      'Turn the study into a report with a link I can forward.',
    ],
    kosten: <>Per page like a run: about 1.5 credits per persona, long pages more; with a generated baseline add 1 credit per 4,000 characters. The report is free.</>,
    achtung: <>
      Give all pages the same way: all as URL or all as text. Mixed, the table is not a fair
      comparison. A report on your own set only gets a link once you agree. Whoever has it sees
      your persona research including proof. You find all reports under{' '}
      <Verweis to="/runs">Runs</Verweis>, where you can also withdraw a link.
    </>,
  },
  ausbau: {
    titel: 'Build out a page step by step',
    kurz: 'Find the best draft section by section, and at the end test against the original.',
    preis: `${AUSBAU.von} to ${AUSBAU.bis} credits`,
    wozu: (
      <>
        The biggest workflow: improve a page section by section, in a fixed order. In each step
        you test several drafts, confirm the winner and say why. At the end the assembled page
        competes against the original, in front of all personas.
      </>
    ),
    prompts: [
      "Let's build out https://[your-page] against [audience].",
      'Test these three drafts for the hero: …',
      'Take draft B. Reason: [why]. On to the next step.',
    ],
    kosten: `A whole page with ${STANDARD.personas} personas costs about ${AUSBAU.von} credits, with a branch at the hero up to ${AUSBAU.bis}. Smaller audiences cost less. Starting and moving on are free.`,
    achtung: <>A single step is complete in itself, the whole page build is not. Check beforehand whether your balance covers all steps.</>,
  },
  ergebnis: {
    titel: 'Report back the real result',
    kurz: 'Which version won in the market. That builds your hit rate.',
    preis: 'free',
    wozu: (
      <>
        Once a tested version was live: report which one won in the market, ideally with
        numbers: clicks and impressions or inquiries and visits. That builds your hit rate,
        meaning how often the simulation was right for you. Results within the noise are saved
        but not counted. You can do this in Claude or directly under <Verweis to="/runs">Runs</Verweis> with
        “Report the result”. For a simulation model, the rate appears once there are ten decidable results, before that “not measured yet”.
      </>
    ),
    prompts: [
      "The new landing page from last week's comparison won: 83 vs 52 inquiries at 1,800 visits each.",
      'How often has 99takes been right for me so far?',
      'Which simulation models are there, and how often were they right?',
    ],
    kosten: 'Free.',
  },
  bibliothek: {
    titel: 'Ideas from the funnel library',
    kurz: 'Search real B2B funnels by industry and have them broken down step by step.',
    preis: 'free',
    wozu: (
      <>
        Search real B2B funnels (landing pages, demo requests, trials, quizzes, calculators,
        pricing pages) by industry and type, have one broken down step by step or see the most
        common patterns in an industry.
      </>
    ),
    prompts: [
      'Show me demo request funnels from the ERP industry in the DACH region.',
      'Which headline patterns are most common in the opening at CMS vendors?',
    ],
    kosten: 'Free.',
  },
};

/** Der Ablauf in der Sprache der App. Fehlt die englische Fassung, bleibt er deutsch. */
function inSprache(a: Ablauf): Ablauf {
  const en = ABLAEUFE_EN[a.id];
  if (sprache() !== 'en' || !en) return a;
  return { ...a, ...en, kosten: { ...a.kosten, text: en.kosten } };
}

const PHASEN: Array<{ id: Phase; titel: string; unter: string; en: { titel: string; unter: string } }> = [
  { id: 'vorbereiten', titel: 'Vorbereiten', unter: `Eine neue Zielgruppe kostet einmalig ${EINRICHTUNG} Credits, das Planen nichts. Danach weißt du, gegen wen du prüfst und was es kostet.`,
    en: { titel: 'Prepare', unter: `A new audience costs ${EINRICHTUNG} credits once, planning is free. Afterwards you know who you test against and what it costs.` } },
  { id: 'pruefen', titel: 'Prüfen', unter: 'Hier werden Credits abgebucht, und Claude nennt den Preis vorher.',
    en: { titel: 'Test', unter: 'This is where credits are charged, and Claude names the price first.' } },
  { id: 'auswerten', titel: 'Auswerten', unter: 'Wochen später, wenn eine Fassung live war.',
    en: { titel: 'Evaluate', unter: 'Weeks later, once a version was live.' } },
  { id: 'ideen', titel: 'Ideen holen', unter: 'Für den Entwurf, bevor es etwas zu prüfen gibt.',
    en: { titel: 'Get ideas', unter: 'For the draft, before there is anything to test.' } },
];

const SKILL_VORLAGE = `---
name: landingpage-check
description: Prüft eine Landingpage mit 99takes gegen unsere Zielgruppe. Nutzen, wenn ich "Landingpage prüfen" sage.
---

1. Frag nach der URL der Seite. Gibt es eine neue Fassung, frag nach ihrem kompletten Text.
2. Nimm das Set [dein-set]. Nenn mir vorher den Preis und warte auf mein Ja.
3. Prüf die ganze Seite. Liegt eine neue Fassung vor, lass sie gegen die aktuelle antreten.
4. Gib mir den Abschnitt, an dem die meisten aussteigen, die drei häufigsten Einwände und einen Vorschlag, der sie adressiert.`;

const SKILL_VORLAGE_EN = `---
name: landingpage-check
description: Tests a landing page with 99takes against our audience. Use when I say "check landing page".
---

1. Ask for the URL of the page. If there is a new version, ask for its complete text.
2. Use the set [your-set]. Tell me the price first and wait for my yes.
3. Test the whole page. If there is a new version, let it compete against the current one.
4. Give me the section where most people drop off, the three most common objections and a suggestion that addresses them.`;

export default function Anleitung() {
  const { session, abmelden } = useSitzung();
  // Verweise wie /guide#personas kommen per Router, nicht per Seitenaufruf —
  // der Browser springt dann nicht von allein zum Anker.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);

  return (
    <AppRahmen email={session?.user?.email ?? ''} abmelden={() => void abmelden()}>
      <Seitenkopf
        titel={t('So arbeitest du mit 99takes in Claude', 'How you work with 99takes in Claude')}
        unter={t(
          <>Du fragst in Claude, 99takes prüft gegen recherchierte Personas deiner Zielgruppe. Bevor etwas Credits kostet, nennt Claude den Preis. Noch nicht verbunden? <Verweis to="/account#verbinden">So geht's</Verweis>. Gerade verbunden? Öffne in Claude einen neuen Chat, ein offener kennt die Werkzeuge noch nicht.</>,
          <>You ask in Claude, and 99takes tests against researched personas from your audience. Before anything costs credits, Claude names the price. Not connected yet? <Verweis to="/account#verbinden">Here's how</Verweis>. Just connected? Open a new chat in Claude, an open one does not know the tools yet.</>,
        )}
      />

      {/* Oben die Wirkung: wie sich 99takes in Claude anfuehlt. Die
          Reihenfolge der Ablaeufe steckt in den vier Szenen, die kopierbaren
          Saetze stehen darunter je Phase. */}
      <SoSiehtEsAus ohneKopf />

      {/* Nach Entwurf 1a („Anleitung und Onboarding", Claude-Design-Projekt
          7f590d08-…, gewaehlt von Robert am 04.10.2026). Die Seite war 8.800 px
          lang, auf dem Telefon 18.000: jeder Ablauf stand mit langem Absatz,
          Werkzeugen und allen Beispielen offen da. Jetzt eine Zeile je Ablauf,
          der Rest klappt auf. Der Inhalt ist derselbe. */}
      <div id="ablaeufe" className="mt-14 scroll-mt-4 border-t border-linie pt-10">
        <h2 className="m-0 mb-2 font-serif text-[1.625rem] leading-[1.12] font-semibold tracking-[-0.015em] text-tinte sm:text-[2rem]">{t('Was du fragen kannst', 'What you can ask')}</h2>
        <p className="mt-0 mb-5 max-w-[62ch] text-[0.9375rem] leading-relaxed text-text-leise">
          {t('Du sagst einfach, was du willst. Claude wählt die Werkzeuge selbst und nennt den Preis, bevor etwas kostet.', 'Just say what you want. Claude picks the tools itself and names the price before anything costs credits.')}
        </p>
        <nav aria-label={t('Phasen', 'Phases')} className="mb-2 flex flex-wrap gap-1.5">
          {PHASEN.map(ph => (
            <Link
              key={ph.id} to={{ hash: `#phase-${ph.id}` }}
              className="inline-flex min-h-8 items-center gap-1.5 rounded-full border border-linie bg-flaeche px-3 text-[0.8125rem] text-text-leise no-underline hover:border-marke/50 hover:text-tinte"
            >
              {t(ph.titel, ph.en.titel)}<span className="text-[0.75rem] text-text-hell tabular-nums">{ABLAEUFE.filter(x => x.phase === ph.id).length}</span>
            </Link>
          ))}
        </nav>
        {PHASEN.map(ph => (
          <Abschnitt key={ph.id} id={`phase-${ph.id}`} titel={t(ph.titel, ph.en.titel)} unter={t(ph.unter, ph.en.unter)}>
            <Panel>
              {ABLAEUFE.filter(x => x.phase === ph.id).map(x => <AblaufZeile key={x.id} a={x} offen={hash === `#${x.id}`} />)}
            </Panel>
          </Abschnitt>
        ))}
      </div>

      {/* Anlass Tobias, 28.09.2026: „Wo sehe ich, dass ich mit 99takes arbeite?"
          Seit 04.10.2026 als Hilfe-Streifen unter den Ablaeufen, die Einzelheiten
          klappen auf. /guide#erkennen oeffnet sie. */}
      <section id="erkennen" aria-labelledby="erkennen-titel" className="mt-14 scroll-mt-4">
        <h2 id="erkennen-titel" className="m-0 mb-3 text-[0.75rem] font-semibold tracking-[0.07em] text-text-leise uppercase">{t('Verbunden und erkannt', 'Connected and recognized')}</h2>
        <Panel className="grid md:grid-cols-3">
          <div className="min-w-0 p-4 sm:p-5">
            <div className="text-[0.9375rem] font-semibold text-tinte">{t('Ist 99takes verbunden?', 'Is 99takes connected?')}</div>
            <p className="mt-1 mb-2.5 text-[0.8125rem] leading-relaxed text-text-leise">{t('Frag in Claude. Nennt Claude dein Guthaben, ist es verbunden.', 'Ask in Claude. If Claude names your balance, it is connected.')}</p>
            <Kopierzeile art="text" text={testsatz()} />
          </div>
          <div className="min-w-0 border-t border-linie-zart p-4 sm:p-5 md:border-t-0 md:border-l">
            <div className="text-[0.9375rem] font-semibold text-tinte">{t('Woran du es siehst', 'How you can tell')}</div>
            <p className="mt-1 mb-0 text-[0.8125rem] leading-relaxed text-text-leise">{t('Jeder Schritt erscheint in Claude als eigene Zeile mit dem Namen von 99takes. Fehlt die Zeile, hat Claude ohne 99takes geantwortet. Vor allem, was Credits kostet, nennt Claude den Preis.', 'Each step shows up in Claude as its own line with the 99takes name. If that line is missing, Claude answered without 99takes. Before anything that costs credits, Claude names the price.')}</p>
          </div>
          <div className="min-w-0 border-t border-linie-zart p-4 sm:p-5 md:border-t-0 md:border-l">
            <div className="text-[0.9375rem] font-semibold text-tinte">{t('Wenn nichts passiert', 'If nothing happens')}</div>
            <p className="mt-1 mb-0 text-[0.8125rem] leading-relaxed text-text-leise">{t('Neuen Chat öffnen, in claude.ai den Connector prüfen, „mit 99takes“ in den Satz nehmen. Fehlt eine neue Funktion, einmal die Werkzeugliste von 99takes aktualisieren.', 'Open a new chat, check the connector in claude.ai, put “with 99takes” in the sentence. If a new feature is missing, refresh the 99takes tool list once.')}</p>
          </div>
        </Panel>
        <details open={hash === '#erkennen'} className="group mt-3 [&_summary::-webkit-details-marker]:hidden">
          <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 text-[0.875rem] font-medium text-tinte hover:text-marke-tief">
            <span aria-hidden className="inline-block text-text-hell transition-transform duration-200 group-open:rotate-90">▸</span>
            {t('Alles dazu im Einzelnen', 'All the details')}
          </summary>
          <p className="mt-3 mb-5 max-w-[60ch] text-[0.9375rem] leading-relaxed text-text-leise">{t(ERKENNEN_UNTER, ERKENNEN_UNTER_EN)}</p>
          <Erkennen />
        </details>
      </section>

      <Abschnitt id="skills" titel={t('Eigene Abläufe als Skill speichern', 'Save your own workflows as a skill')}>
        <p className="mt-0 mb-3 max-w-[64ch] text-[0.9375rem] leading-relaxed text-text-leise">
          {t(<>
            99takes bringt keine eigenen Skills oder Befehle mit. Die Abläufe oben stecken in den
            Werkzeugen, und Claude wählt sie aus deiner Frage. Fährst du denselben Ablauf öfter,
            kannst du ihn in Claude Code als Skill ablegen: eine Datei unter{' '}
            <code className="font-mono text-[0.8125rem]">.claude/skills/landingpage-check/SKILL.md</code>{' '}
            in deinem Projekt. Danach reicht „Landingpage prüfen“.
          </>, <>
            99takes brings no skills or commands of its own. The workflows above are built into the
            tools, and Claude picks them from your question. If you run the same workflow often,
            you can save it as a skill in Claude Code: a file at{' '}
            <code className="font-mono text-[0.8125rem]">.claude/skills/landingpage-check/SKILL.md</code>{' '}
            in your project. After that, “check landing page” is enough.
          </>)}
        </p>
        <details className="group [&_summary::-webkit-details-marker]:hidden">
          <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 text-[0.875rem] font-medium text-tinte hover:text-marke-tief">
            <span aria-hidden className="inline-block text-text-hell transition-transform duration-200 group-open:rotate-90">▸</span>
            {t('Vorlage zeigen', 'Show template')}
          </summary>
          <div className="mt-3"><Kopierzeile text={t(SKILL_VORLAGE, SKILL_VORLAGE_EN)} /></div>
        </details>
        <p className="mt-3 mb-0 text-[0.8125rem] leading-relaxed text-text-hell">
          {t(<>
            Die Beschreibung oben in der Datei entscheidet, wann Claude den Skill benutzt. Das
            Set ersetzt du durch den Namen aus <Verweis to="/personas">Zielgruppen</Verweis>.
          </>, <>
            The description at the top of the file decides when Claude uses the skill. Replace
            the set with the name from <Verweis to="/personas">Audiences</Verweis>.
          </>)}
        </p>
      </Abschnitt>

      <Abschnitt titel={t('Was 99takes nicht beantwortet', 'What 99takes does not answer')}>
        <p className="m-0 max-w-[64ch] text-[0.9375rem] leading-relaxed text-text-leise">
          {t(<>
            Im A/B-Test lesen die Personas den Text, wie prominent er steht, und bei einer URL eine
            Beschreibung des Layouts: was über der Falz steht, wie viele Knöpfe konkurrieren, wie
            lang das Formular ist. Ob Farben, Bilder und Schrift gefallen, gehört nicht in dieses
            Urteil, denn A und B sehen gleich aus. Dafür gibt es den Design-Check: Dort sehen die
            Personas die Seite selbst und sagen, wie sie wirkt. Was 99takes nicht beantwortet, ist,
            wie viele Leute im Markt am Ende wirklich kaufen. Dafür sind deine echten Zahlen die
            bessere Quelle, und gemeldet machen sie 99takes genauer.
          </>, <>
            In the A/B test the personas read the text, how prominently it is placed and, for a URL,
            a description of the layout: what is above the fold, how many buttons compete, how long
            the form is. Whether colors, images and fonts appeal does not belong in this verdict,
            because A and B look the same. That is what the design check is for: there the personas
            see the page itself and say how it comes across. What 99takes does not answer is how
            many people in the market will actually buy in the end. Your real numbers are the
            better source for that, and once reported they make 99takes more accurate.
          </>)}
        </p>
      </Abschnitt>
    </AppRahmen>
  );
}

/**
 * Ein Ablauf als Zeile (Entwurf 1a, 04.10.2026): links Titel, Preis und ein
 * Satz, rechts der wichtigste Satz zum Kopieren. Erklaerung, Kosten im
 * Einzelnen, Werkzeuge und die weiteren Beispiele klappen auf. `offen`: per
 * /guide#<id> angesprungen, dann steht alles offen.
 */
function AblaufZeile({ a: ablauf, offen }: { a: Ablauf; offen: boolean }) {
  const a = inSprache(ablauf);
  const [erster, ...weitere] = a.prompts;
  const klappe = 'group/k [&_summary::-webkit-details-marker]:hidden';
  const knopf = 'inline-flex cursor-pointer list-none items-center gap-1.5 text-[0.8125rem] font-medium text-text-leise hover:text-marke-tief';
  const pfeil = <span aria-hidden className="inline-block text-text-hell transition-transform duration-200 group-open/k:rotate-90">▸</span>;
  return (
    <section id={a.id} className="grid scroll-mt-4 gap-x-8 gap-y-3 border-t border-linie-zart px-4 py-4 first:border-t-0 sm:px-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <h3 className="m-0 text-[0.9375rem] font-semibold tracking-[-0.005em] text-tinte">{a.titel}</h3>
          <Marke art={a.kosten.frei ? 'gut' : 'marke'}>{a.preis}</Marke>
        </div>
        <p className="mt-1 mb-0 text-[0.875rem] leading-relaxed text-text-leise">{a.kurz}</p>
        <div className="mt-2 flex flex-col gap-2">
          <details open={offen} className={klappe}>
            <summary className={knopf}>{pfeil}{t('Wie es funktioniert', 'How it works')}</summary>
            <div className="mt-2">
              <p className="mt-0 mb-2.5 text-[0.875rem] leading-relaxed text-text-leise">{a.wozu}</p>
              <p className="mt-0 mb-2 text-[0.8125rem] leading-relaxed text-text-leise">
                <span className="font-medium text-text">{t('Kosten: ', 'Cost: ')}</span>{a.kosten.text}
              </p>
              <div className="flex flex-wrap items-center gap-1.5 text-[0.8125rem] text-text-hell">
                <span>{t('Dahinter:', 'Behind it:')}</span>
                {a.werkzeuge.map(w => (
                  <code key={w} className="rounded bg-grund px-1.5 py-0.5 font-mono text-[0.75rem] text-text-leise">{w}</code>
                ))}
              </div>
              {a.achtung && (
                <p className="mt-2.5 mb-0 rounded-lg bg-grund px-3.5 py-2.5 text-[0.8125rem] leading-relaxed text-text-leise">{a.achtung}</p>
              )}
            </div>
          </details>
          {weitere.length > 0 && (
            <details open={offen} className={klappe}>
              <summary className={knopf}>{pfeil}{weitere.length === 1 ? t('1 weiteres Beispiel', '1 more example') : t(`${weitere.length} weitere Beispiele`, `${weitere.length} more examples`)}</summary>
              <ul className="m-0 mt-2 list-none space-y-2 p-0">
                {weitere.map(p => <li key={p}><Kopierzeile text={p} art="text" /></li>)}
              </ul>
            </details>
          )}
        </div>
      </div>
      <div className="min-w-0">
        {erster && <Kopierzeile text={erster} art="text" />}
      </div>
    </section>
  );
}
